import { useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { CHALETS, DECORATIONS } from "@/lib/data";
import { calcQuote } from "@/lib/pricing";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { ChaletPicker, DateStep } from "@/components/site/DateStep";
import { DecorationPicker } from "@/components/site/Options";
import GuestForm from "@/components/site/GuestForm";
import Summary from "@/components/site/Summary";
import SuccessDialog from "@/components/site/SuccessDialog";

const EMPTY = {
  nome_completo: "", data_nascimento: "", cpf: "", email: "", whatsapp: "", nome_acompanhante: "",
  numero_hospedes: "2", pet: false, forma_pagamento: "", parcelas: 1, solicitacao_especial: "",
};

const Step = ({ n, title, children, testid }) => (
  <Reveal>
    <div data-testid={testid} className="border-t border-[#e2ded6] pt-8">
      <div className="mb-6 flex items-baseline gap-4">
        <span className="text-sm font-bold text-[#c86d4b]">{n}</span>
        <h3 className="text-xl font-bold uppercase tracking-tight text-[#163323] sm:text-2xl">{title}</h3>
      </div>
      {children}
    </div>
  </Reveal>
);

const validate = (f, range, decoOn, decoId) => {
  const e = {};
  if (!range?.from || !range?.to || range.to <= range.from) e.datas = "Selecione as datas de entrada e saída.";
  if (f.nome_completo.trim().length < 3) e.nome_completo = "Informe seu nome completo.";
  if (!f.data_nascimento) e.data_nascimento = "Informe a data de nascimento.";
  if (f.cpf.replace(/\D/g, "").length !== 11) e.cpf = "CPF inválido.";
  if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "E-mail inválido.";
  if (f.whatsapp.replace(/\D/g, "").length < 10) e.whatsapp = "Telefone inválido.";
  if (f.nome_acompanhante.trim().length < 2) e.nome_acompanhante = "O nome do acompanhante é obrigatório.";
  if (!(Number(f.numero_hospedes) >= 1)) e.numero_hospedes = "Informe o número de hóspedes.";
  if (!f.forma_pagamento) e.forma_pagamento = "Escolha a forma de pagamento.";
  if (decoOn && !decoId) e.deco = "Escolha uma opção de decoração.";
  return e;
};

export default function Booking({ chaletId, setChaletId }) {
  const chalet = CHALETS.find((c) => c.id === chaletId) ?? CHALETS[0];
  const [range, setRange] = useState();
  const [decoOn, setDecoOn] = useState(false);
  const [decoId, setDecoId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const deco = DECORATIONS.find((d) => d.id === decoId) ?? null;
  const quote = useMemo(() => calcQuote({ chalet, range, pet: form.pet, deco }), [chalet, range, form.pet, deco]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const e = validate(form, range, decoOn, decoId);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error(Object.values(e)[0]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/reservas`, {
        ...form,
        chalet_id: chalet.id,
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        numero_hospedes: Number(form.numero_hospedes),
        decoracao: decoOn ? decoId : null,
        parcelas: form.forma_pagamento === "cartao" ? form.parcelas : 1,
      });
      setResult(data ?? {});
      setForm(EMPTY);
      setRange(undefined);
      setDecoOn(false);
      setDecoId(null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível enviar sua reserva. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reservar" data-testid="reservation-form" className="bg-[#faf7f2] py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="max-w-3xl">
          <Eyebrow>Solicitação de reserva</Eyebrow>
          <h2 className="mt-6 text-4xl font-bold uppercase leading-[1] tracking-[-0.03em] text-[#163323] sm:text-5xl lg:text-6xl">
            Escolha suas datas.<br /><span className="text-[#c86d4b]">Nós cuidamos do resto.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-12 lg:grid-cols-12">
          <div className="space-y-12 lg:col-span-8">
            <Step n="01" title="Chalé e datas" testid="step-dates">
              <ChaletPicker chaletId={chalet.id} setChaletId={setChaletId} />
              <div className="mt-5"><DateStep range={range} setRange={setRange} /></div>
              {errors.datas && <p className="mt-2 text-xs font-bold text-[#b42318]">{errors.datas}</p>}
            </Step>
            <Step n="02" title="Decoração romântica" testid="step-decoration">
              <DecorationPicker enabled={decoOn} setEnabled={setDecoOn} decoId={decoId} setDecoId={setDecoId} />
              {errors.deco && <p className="mt-2 text-xs font-bold text-[#b42318]">{errors.deco}</p>}
            </Step>
            <Step n="03" title="Seus dados" testid="step-guest">
              <GuestForm form={form} set={set} errors={errors} total={quote.total} />
            </Step>
          </div>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <Summary chalet={chalet} range={range} quote={quote} deco={decoOn ? deco : null} form={form} loading={loading} onSubmit={submit} />
            </div>
          </div>
        </div>
      </div>
      <SuccessDialog result={result} onClose={() => setResult(null)} />
    </section>
  );
}
